import { Outlet, NavLink, Link } from 'react-router-dom';
import { useUIStore } from '../stores/ui';
import { useAuthStore } from '../stores/auth';
import {
  LayoutDashboard,
  CheckSquare,
  Calendar,
  Clock,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Command,
} from 'lucide-react';
import clsx from 'clsx';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
  { path: '/calendar', icon: Calendar, label: 'Calendar' },
  { path: '/planner', icon: Clock, label: 'Planner' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export function Layout() {
  const { isSidebarCollapsed, toggleSidebar, setCommandPaletteOpen } = useUIStore();
  const { user, logout } = useAuthStore();

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={clsx(
          'flex flex-col border-r border-border bg-surface transition-all duration-300',
          isSidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="flex h-14 items-center justify-between border-b border-border px-3">
          <Link to="/" className="flex items-center gap-2">
            {isSidebarCollapsed ? (
              <img
                src="/personal-chotu-logo-base.png"
                alt="Chotu"
                className="h-8 w-8"
              />
            ) : (
              <img
                src="/personal-chotu-logo-full.png"
                alt="Chotu"
                className="h-8"
              />
            )}
          </Link>
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
          >
            {isSidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Command Palette Trigger */}
        <div className="p-3">
          <button
            onClick={() => setCommandPaletteOpen(true)}
            className={clsx(
              'flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-secondary hover:border-accent/50 hover:text-text-primary',
              isSidebarCollapsed && 'justify-center px-2'
            )}
          >
            <Command className="h-4 w-4" />
            {!isSidebarCollapsed && (
              <>
                <span className="flex-1 text-left">Search...</span>
                <kbd className="kbd">⌘K</kbd>
              </>
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-3">
          {navItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent-muted text-accent'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                  isSidebarCollapsed && 'justify-center px-2'
                )
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {!isSidebarCollapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-border p-3">
          <div
            className={clsx(
              'flex items-center gap-3 rounded-lg px-3 py-2',
              isSidebarCollapsed && 'justify-center px-2'
            )}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-muted text-accent">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            {!isSidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium">{user?.name || 'User'}</p>
                <p className="truncate text-xs text-text-tertiary">{user?.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            className={clsx(
              'mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover hover:text-error',
              isSidebarCollapsed && 'justify-center px-2'
            )}
          >
            <LogOut className="h-4 w-4" />
            {!isSidebarCollapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

